import { DateInputLocalized } from '@components'

import { DateTimeFormat, Flex, Icon, Spacing, Tag, Text, Tooltip } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useWatch } from '../../../../../../../../../../hooks'
import { getCurrentLocale } from '../../../../../../../../../../utils/getCurrentLocale'
import { useInvoiceEditorForm } from '../../../../../../contexts/invoiceEditorFormContext'
import { ExpandableSection } from '../ExpandableSection'

export const QuoteDateOfSaleOrService = (): ReactElement => {
  const { t } = useTranslation()
  const { invoiceEditorForm, trackInvoiceEditor } = useInvoiceEditorForm()
  const { FormItem, control } = invoiceEditorForm

  const dateValue = useWatch({ control, name: 'dateOfSaleOrService' })

  const handleToggle = useCallback(
    (isSectionExpanded: boolean) => {
      trackInvoiceEditor(`Form - Estimated date of sale or service ${isSectionExpanded ? 'expanded' : 'collapsed'}`)
    },
    [trackInvoiceEditor],
  )

  const handleDatePickerOpen = useCallback(() => {
    trackInvoiceEditor('Form - Estimated date of sale or service picker opened')
  }, [trackInvoiceEditor])

  const collapsedTags = dateValue ? (
    <Tag size="sm" type="subtle">
      {DateTimeFormat.getFormattedDate(dateValue, getCurrentLocale())}
    </Tag>
  ) : null

  const label = (
    <Flex as="span" flexDirection="row" gap={Spacing.S6}>
      <Text colorVariant="secondary">
        {t('external_invoices.editor.form.field.estimated_date_of_sale_or_service.field_label')}
      </Text>
      <Tooltip
        as="span"
        label={t('external_invoices.editor.form.field.estimated_date_of_sale_or_service.tooltip')}
        placement="top"
      >
        <Icon icon="info" size="xs" />
      </Tooltip>
    </Flex>
  )

  return (
    <ExpandableSection
      collapsedTags={collapsedTags}
      emptyLabel={t('external_invoices.editor.form.field.estimated_date_of_sale_or_service.add.label')}
      hasValue={!!dateValue}
      onToggle={handleToggle}
      toggleLabel={t('external_invoices.editor.form.field.estimated_date_of_sale_or_service.label')}
    >
      <FormItem
        name="dateOfSaleOrService"
        label={label}
        render={({ field: { onChange, ...props } }) => (
          <DateInputLocalized
            {...props}
            allowClear
            onChange={(date) => {
              onChange(date)
              trackInvoiceEditor('Form - Estimated date of sale or service selected', { date })
            }}
            onClear={() => onChange(null)}
            onOpen={handleDatePickerOpen}
            size="sm"
          />
        )}
      />
    </ExpandableSection>
  )
}
